import { MenuItem, Typography, useTheme, Box } from "@mui/material";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { Link as RouteLink, useLocation, useParams } from "react-router-dom";

import { NavMenuItem } from "../../../api/dtos/dtos";
import Dropdown from "../Dropdown";
import { IHasChildren } from "../types/IHasChildren";

interface INavButtonProps extends IHasChildren {
    isDefault?: string;
    to: string;
    menuItems?: NavMenuItem[];
    isSubItem?: boolean;
}

const NavButton: FC<INavButtonProps> = ({
    isDefault,
    to,
    menuItems,
    children,
    isSubItem = false
}) => {
    const theme = useTheme();

    const { page } = useParams();
    const location = useLocation();

    const [open, setOpen] = useState<boolean>(false);
    const anchor = useRef<HTMLDivElement>(null);

    const isExternal = useMemo(() => {
        return to?.startsWith("http://") || to?.startsWith("https://");
    }, [to]);

    const isSelected = useMemo(() => {
        return (
            (page &&
                (to?.endsWith(page) ||
                    !!menuItems?.find((x) => x.link?.endsWith(page)))) ||
            location.pathname.endsWith(to) ||
            (isDefault && location.pathname?.endsWith(isDefault))
        );
    }, [page, location, to, menuItems, isDefault]);

    const handleOpenMenu = useCallback(() => setOpen(true), []);
    const handleCloseMenu = useCallback(() => setOpen(false), []);

    const commonHoverStyles = {
        "&:hover": {
            backgroundColor: "transparent",
            borderBottom: `4px solid ${theme.palette.secondary.main}`,
            color: `${theme.palette.secondary.main} !important`,
        },
        "&:hover > p, &:hover > a, &:hover svg": {
            color: `${theme.palette.secondary.main} !important`,
            fill: `${theme.palette.secondary.main} !important`,
        },
    };

    return (
        <div
            ref={anchor}
            style={{ position: "relative" }}
            onMouseEnter={handleOpenMenu}
            onMouseLeave={handleCloseMenu}
        >
            {!isSubItem ? (
                <MenuItem
                    component={isExternal ? "a" : RouteLink}
                    href={isExternal ? to : undefined}
                    to={!isExternal ? to : undefined}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    onClick={(e) => {
                        if (isExternal) e.stopPropagation();
                    }}
                    sx={{
                        borderBottom: `4px solid ${
                            isSelected ? theme.palette.secondary.main : "transparent"
                        }`,
                        padding: "14px",
                        color: isSelected
                            ? `${theme.palette.secondary.main} !important`
                            : "#333 !important",
                        "& svg": {
                            color: isSelected
                                ? `${theme.palette.secondary.main} !important`
                                : "#333 !important",
                            fill: isSelected
                                ? `${theme.palette.secondary.main} !important`
                                : "#333 !important",
                        },
                        ...commonHoverStyles,
                    }}
                >
                    {children}
                </MenuItem>
            ) : (
                <Box
                    sx={{
                        padding: "10px 14px",
                        borderBottom: `4px solid transparent`,
                        cursor: "pointer",
                        color: isSelected ? theme.palette.secondary.main : "#333",
                        ...commonHoverStyles,
                    }}
                    onClick={() => {
                        if (isExternal) window.open(to, "_blank", "noopener,noreferrer");
                    }}
                >
                    {!isExternal ? (
                        <RouteLink
                            to={to}
                            style={{
                                textDecoration: "none",
                                color: isSelected ? theme.palette.secondary.main : "#333",
                                display: "block",
                                width: "100%",
                            }}
                        >
                            {children}
                        </RouteLink>
                    ) : (
                        <div>{children}</div>
                    )}
                </Box>
            )}

            {menuItems && menuItems.length > 0 && (
                <Dropdown anchor={anchor.current} open={open}>
                    {[...menuItems]
                        .sort((a, b) => a.rank - b.rank)
                        .map((x) => (
                            <NavButton
                                key={`nav-${x.rank}-${x.id}`}
                                to={x.link}
                                menuItems={x.items}
                                isSubItem
                            >
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        whiteSpace:' nowrap'
                                    }}
                                >
                                    {x.name}
                                </Typography>
                            </NavButton>
                        ))}
                </Dropdown>
            )}
        </div>
    );
};

export default NavButton;